"use client";

import React, { useEffect, useRef } from "react";
import { ShieldCheck, Lightbulb, Activity } from "lucide-react";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const coreValues = [{
  title: "Ακεραιότητα",
  description: "Διαφανείς διαδικασίες, απόλυτη εχεμύθεια και αδιαπραγμάτευτη ηθική στη διαχείριση των κρίσιμων δεδομένων της επιχείρησής σας.",
  icon: ShieldCheck
}, {
  title: "Καινοτομία",
  description: "Συνεχής παρακολούθηση και ενσωμάτωση τεχνολογιών αιχμής για την προληπτική αντιμετώπιση των πιο σύγχρονων ψηφιακών απειλών.",
  icon: Lightbulb
}, {
  title: "Ανθεκτικότητα",
  description: "Σχεδιασμός υποδομών που δεν προστατεύουν απλώς, αλλά εξασφαλίζουν την άμεση ανάκαμψη και την αδιάλειπτη λειτουργία σας υπό οποιεσδήποτε συνθήκες.",
  icon: Activity
}];
export default function OurPhilosophy() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: cardsRef,
    isVisible: areCardsVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    let animationFrameId: number;
    let particles: Particle[] = [];
    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = canvas.parentElement?.clientHeight || window.innerHeight;
      initParticles();
    };
    class Particle {
      x: number;
      y: number;
      vx: number;
      vy: number;
      size: number;
      constructor() {
        this.x = Math.random() * canvas!.width;
        this.y = Math.random() * canvas!.height;
        this.vx = (Math.random() - 0.5) * 0.5;
        this.vy = (Math.random() - 0.5) * 0.5;
        this.size = Math.random() * 1.5 + 0.5;
      }
      update() {
        this.x += this.vx;
        this.y += this.vy;
        if (this.x < 0 || this.x > canvas!.width) this.vx = -this.vx;
        if (this.y < 0 || this.y > canvas!.height) this.vy = -this.vy;
      }
      draw() {
        if (!ctx) return;
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
        ctx.fillStyle = "rgba(0, 230, 255, 0.3)";
        ctx.fill();
      }
    }
    const initParticles = () => {
      particles = [];
      const numberOfParticles = Math.floor(canvas.width * canvas.height / 15000);
      for (let i = 0; i < numberOfParticles; i++) {
        particles.push(new Particle());
      }
    };
    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      for (let i = 0; i < particles.length; i++) {
        particles[i].update();
        particles[i].draw();
        for (let j = i; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const distance = Math.sqrt(dx * dx + dy * dy);
          if (distance < 120) {
            ctx.beginPath();
            ctx.strokeStyle = `rgba(0, 230, 255, ${0.1 - distance / 1200})`;
            ctx.lineWidth = 0.5;
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.stroke();
          }
        }
      }
      animationFrameId = requestAnimationFrame(animate);
    };
    window.addEventListener("resize", resizeCanvas);
    resizeCanvas();
    animate();
    return () => {
      window.removeEventListener("resize", resizeCanvas);
      cancelAnimationFrame(animationFrameId);
    };
  }, []);
  return <section id="η-φιλοσοφία-μας" className="dark bg-background text-foreground relative overflow-hidden py-24 lg:py-32">
      {}
      <canvas ref={canvasRef} className="absolute inset-0 z-0 pointer-events-none opacity-60" aria-hidden="true" />

      {}
      <div className="absolute inset-0 z-0 bg-gradient-to-b from-background/20 via-background/80 to-background pointer-events-none" />

      <div className="relative z-10 container mx-auto px-4 md:px-6">
        {}
        <div ref={headerRef} className={`max-w-4xl mx-auto text-center mb-20 transition-all duration-1000 ease-out ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-7xl font-bold tracking-tight text-primary-foreground mb-6 leading-tight">
            Εταιρική ασφάλεια για το <span className="text-primary">μικρό γραφείο.</span>
          </h2>
          <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto font-sans">
            Στην <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1592747/img/ds_services_logo.png" alt="Ds-Services" className="h-7 inline-block align-middle" />, πιστεύουμε ότι η κορυφαία τεχνολογία και η απόλυτη ασφάλεια δεν αποτελούν προνόμιο μόνο των μεγάλων οργανισμών.
          </p>
        </div>

        {}
        <div ref={cardsRef} className={`grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto transition-all duration-1000 delay-300 ease-out ${areCardsVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          {coreValues.map((value, i) => {
          const Icon = value.icon;
          return <div data-index={i} key={i} className="h-full">
                <Card className="h-full bg-card/40 backdrop-blur-md border-border/50 hover:border-primary/50 transition-colors duration-300 shadow-lg shadow-black/20">
                  <CardHeader className="pb-4">
                    <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4 border border-primary/20">
                      <Icon className="w-6 h-6 text-primary" />
                    </div>
                    <CardTitle className="text-xl font-bold text-card-foreground font-sans">
                      {value.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-base text-muted-foreground leading-relaxed">
                      {value.description}
                    </CardDescription>
                  </CardContent>
                </Card>
              </div>;
        })}
        </div>
      </div>
    </section>;
}